import { API } from '@/src/api/api'
import type { LiveLocation, LocationsInfoDevice, LocationsInfoResponse, StagingLocation } from '@/src/api/types/backend/locations-info.api'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { useMeterStore } from '@shared/domain/meters/store/meter_store'
import { t } from 'i18next'
import { createContext, useContext, useEffect, useState } from 'react'
import { safeLocalStorage } from '../shared/safeStorage'

export type EnrichedDevice = LocationsInfoDevice & {
  buildingName: string
  locationId: string
  locationPgId: number
  locationName: string
  companyId: string
}

export type AllLocation = Omit<LocationsInfoResponse['locations'][0], 'devices'>

export type EnrichedLocation =
  | (Omit<StagingLocation, 'devices'> & { devices: EnrichedDevice[] })
  | (Omit<LiveLocation, 'devices'> & { devices: EnrichedDevice[] })

export type EnrichedLocations = [AllLocation, ...EnrichedLocation[]]

export type EnrichedLocationsInfoRegion = Omit<LocationsInfoResponse['regions'][0], 'locations'> & {
  locations: EnrichedLocations
}

export type EnrichedRegions = [AllRegionsRegion, ...EnrichedLocationsInfoRegion[]]

export type AllRegionsRegion = { id: 'allGroups'; name: string; locations: EnrichedLocations }
export type EnrichedLocationsInfo = Omit<LocationsInfoResponse, 'locations' | 'regions'> & {
  locations: EnrichedLocations
  regions: EnrichedRegions | []
}

// Define the type for your context value
interface LocationsContextBaseValue {
  isLoading: boolean // flag to indicate if the context is currently loading data
  hasLocations: boolean
  companyPgId: number | undefined
  selectedLocationIndex: number
  selectedRegionIndex: number
  selectedDeviceIndex: number
  selectedBuildingIndex: number
  userCanSeeAllLocations: boolean
  currentBuildings: EnrichedLocation['buildings'][1][] | undefined
  currentRegion: EnrichedRegions[0] | EnrichedRegions[1] | null
  isLocation: typeof isLocation
  isAllLocation: typeof isAllLocation
  isStagingLocation: typeof isStagingLocation
  isLiveLocation: typeof isLiveLocation
  isBuilding: typeof isBuilding
  isAllBuilding: typeof isAllBuilding
  setSelectedRegionIndex: (index: number) => void
  setSelectedLocationIndex: (index: number) => void
  setSelectedDeviceIndex: (index: number) => void
  setSelectedBuildingIndex: (index: number) => void
  resetStorageData: () => void
  selectFriendlyFormat: { value: number; label: string }[]
  pgIdOptions: { value: number; label: string }[]
  firebaseIdOptions: { value: string; label: string }[]
  companyFirebaseIdOptions: { value: string; label: string }[]
  refresh: () => void
  getHierarchySelection: () => HierarchySelection
  getSelectedMainDevicesIds: (locationIndex?: number, buildingIndex?: number) => string[]
  getDevices: (regionIndex?: number, locationIndex?: number, buildingIndex?: number) => EnrichedDevice[]
}

interface LocationsContextUninitialized extends LocationsContextBaseValue {
  isInitialized: false
  regions: []
  allLocations: []
  locations: undefined
  currentLocation: null
}

export interface LocationsContextInitialized extends LocationsContextBaseValue {
  isInitialized: true
  regions: EnrichedRegions
  allLocations: EnrichedLocations
  locations: EnrichedLocations
  currentLocation: EnrichedLocations[0] | EnrichedLocations[1]
}

export type LocationsContextValue = LocationsContextUninitialized | LocationsContextInitialized

const LocationsContext = createContext<LocationsContextValue | undefined>(undefined)

export const useLocationsContext = (): LocationsContextValue => {
  const context = useContext(LocationsContext)
  if (!context) {
    throw new Error('useLocationsContext must be used within a LocationsProvider')
  }
  return context
}

// ponytail: PDF pages have no locations provider; their renderers can keep the saved series labels.
export const useOptionalLocationsContext = (): LocationsContextValue | undefined => useContext(LocationsContext)

export const ALL_LOCATIONS: number = -1
export const DEFAULT_BUILDING_NAME: string = 'Main building'

export const checkIfCompanyHasLocations = (locations, companyId) => {
  return locations ? companyId !== locations[1]?.id || companyId === '02bcIgjbHD44N6NnXAIm' : false
}

/**
 *
 * Type guard that asserts that the location is a specific location.
 * We need this extra type guard because TS does not work well when asserting in array methods like filter.
 */
export function isLocation(location: AllLocation | EnrichedLocation): location is EnrichedLocation {
  return location.id !== 'allLocations' // or any other distinguishing characteristic
}

/**
 *
 * Type guard that asserts that the location is not the "All Locations"
 */
export function isAllLocation(location: AllLocation | EnrichedLocation): location is AllLocation {
  return location.id === 'allLocations' // or any other distinguishing characteristic
}

/**
 *
 * Type guard that asserts that the building is not the "All Buildings" building
 */
export function isBuilding(
  building: LocationsInfoResponse['locations'][0]['buildings'][0] | LocationsInfoResponse['locations'][0]['buildings'][1]
): building is LocationsInfoResponse['locations'][0]['buildings'][1] {
  return building.id !== 'allBuildings'
}

export function isAllBuilding(
  building: LocationsInfoResponse['locations'][0]['buildings'][0] | LocationsInfoResponse['locations'][0]['buildings'][1]
): building is LocationsInfoResponse['locations'][0]['buildings'][0] {
  return building.id === 'allBuildings'
}

export function isStagingLocation(location: EnrichedLocation): location is EnrichedLocation & StagingLocation {
  return location.lifecycleStatus === 'staging'
}

export function isLiveLocation(location: EnrichedLocation): location is EnrichedLocation & LiveLocation {
  return location.lifecycleStatus === 'live'
}

export const getLocationNameById = (locationId: string, rawLocations: EnrichedLocations | undefined) => {
  const locationIdNumber = Number.parseInt(locationId)
  const foundLocation = rawLocations?.find(it => {
    const castLocation = it as { rawLocationPgId: number | undefined; locationPgId: number }
    const allLocationMatch = castLocation.rawLocationPgId === locationIdNumber
    const secondaryLocationMatch = castLocation.locationPgId === locationIdNumber
    return allLocationMatch || secondaryLocationMatch
  })
  return foundLocation?.name || ''
}

export interface HierarchySelection {
  buildingId: number | undefined
  locationId: number | undefined
  regionId: number | string | undefined
  companyId: number | undefined

  locationFirebaseId: string | undefined
  companyFirebaseId: string | undefined
}

export const LocationsProvider = ({ children }) => {
  const [regions, setRegions] = useState<EnrichedRegions | []>([]) // all regions of company
  const [allLocations, setAllLocations] = useState<EnrichedLocations | []>([]) // all locations of company
  const [locations, setLocations] = useState<EnrichedLocations | undefined>() // locations of currently selected region
  const [companyPgId, setCompanyPgId] = useState<number>()
  const [selectedRegionIndex, setSelectedRegionIndex] = useState<number>(0)
  const [selectedLocationIndex, setSelectedLocationIndex] = useState<number>(0)
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState<number>(0)
  const [selectedBuildingIndex, setSelectedBuildingIndex] = useState<number>(0)
  const [userCanSeeAllLocations, setUserCanSeeAllLocations] = useState<boolean>(true)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const { getAvailableYears, getAvailableYearsByCompany } = useMeterStore()

  const session = useSessionStore()
  const companyId = session.user?.companyId || ''
  const userId = session.user?.id || ''

  const [refire, setRefire] = useState<number>(0)
  const hasLocations = checkIfCompanyHasLocations(locations, companyId)

  useEffect(() => {
    const controller = new AbortController()

    const effect = async () => {
      try {
        setIsLoading(true)
        const locationsInfoRes = await API.locationsinfo.fetch(companyId, userId, { signal: controller.signal })
        const locationsInfo = getEnrichedLocationsInfo(locationsInfoRes.data, session.user?.companyName)

        const lsRegionIndex = safeLocalStorage.getItem('selectedRegionIndex')
        const lsRegionIndexNumber = lsRegionIndex ? Number(lsRegionIndex) : null
        const regionIndex = lsRegionIndexNumber ?? 0

        const lsLocationIndex = safeLocalStorage.getItem('selectedLocationIndex')
        const lsLocationIndexNumber = lsLocationIndex ? Number(lsLocationIndex) : null
        const locationIndex = lsLocationIndexNumber ?? (locationsInfo.locations?.length > 2 && locationsInfo.userCanSeeAllLocations ? 0 : 1)

        const lsBuildingIndex = safeLocalStorage.getItem('selectedBuildingIndex')
        const lsBuildingIndexNumber = lsBuildingIndex ? Number(lsBuildingIndex) : null
        const buildingIndex = lsBuildingIndexNumber ?? (locationsInfo.locations[locationIndex].buildings.length > 2 ? 0 : 1)

        setSelectedRegionIndex(regionIndex)
        setLocations(locationsInfo.regions[regionIndex].locations) // locations array always holds the locations of the selected region
        setSelectedLocationIndex(locationIndex)
        setSelectedBuildingIndex(buildingIndex)
        setAllLocations(locationsInfo.locations)
        setUserCanSeeAllLocations(locationsInfo.userCanSeeAllLocations)
        setRegions(locationsInfo.regions)
        setCompanyPgId(locationsInfo.locations[1].companyPgId)
        setIsInitialized(true)
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Error fetching locations info', err)
      } finally {
        setIsLoading(false)
      }
    }

    effect()

    return () => {
      controller.abort()
    }
  }, [refire])

  useEffect(() => {
    if (!locations?.length) return
    if (selectedLocationIndex && locations[selectedLocationIndex].locationPgId !== ALL_LOCATIONS) {
      getAvailableYears(locations[selectedLocationIndex].locationPgId)
    } else {
      getAvailableYearsByCompany(companyId)
    }
  }, [selectedLocationIndex, locations])

  return (
    <LocationsContext.Provider
      //@ts-ignore
      value={{
        isInitialized,
        isLoading,
        regions,
        hasLocations,
        locations,
        allLocations,
        companyPgId,
        selectedLocationIndex,
        selectedRegionIndex,
        selectedDeviceIndex,
        selectedBuildingIndex,
        currentBuildings:
          selectedBuildingIndex === 0
            ? locations?.[0]?.buildings.slice(1)
            : locations?.[selectedLocationIndex]?.buildings[selectedBuildingIndex]
              ? [locations?.[selectedLocationIndex]?.buildings[selectedBuildingIndex]]
              : [],
        currentRegion: regions ? regions[selectedRegionIndex] : null,
        isAllLocation,
        isLocation,
        isAllBuilding,
        isBuilding,
        isStagingLocation,
        isLiveLocation,
        setSelectedRegionIndex: index => {
          setSelectedRegionIndex(index)
          // locations always holds the locations of the selected region
          setLocations(regions[index].locations)
          safeLocalStorage.setItem('selectedRegionIndex', String(index))
        },
        currentLocation: locations ? locations[selectedLocationIndex] : null,
        setSelectedLocationIndex: index => {
          setSelectedLocationIndex(index)
          setSelectedDeviceIndex(0)
          safeLocalStorage.setItem('selectedLocationIndex', String(index))
        },
        setSelectedDeviceIndex: index => {
          setSelectedDeviceIndex(index)
          safeLocalStorage.setItem('selectedDeviceIndex', String(index))
        },
        setSelectedBuildingIndex: index => {
          setSelectedBuildingIndex(index)
          safeLocalStorage.setItem('selectedBuildingIndex', String(index))
        },
        resetStorageData: () => {
          safeLocalStorage.removeItem('selectedRegionIndex')
          safeLocalStorage.removeItem('selectedLocationIndex')
          safeLocalStorage.removeItem('selectedBuildingIndex')
          safeLocalStorage.removeItem('selectedDeviceIndex')
        },
        selectFriendlyFormat:
          locations?.map((location, idx) => {
            return {
              value: idx,
              label: location.name,
            }
          }) ?? [],
        pgIdOptions:
          locations
            ?.filter((_, idx) => idx > 0)
            .map((location, idx) => {
              return {
                value: location.locationPgId,
                label: location.name,
              }
            }) ?? [],
        firebaseIdOptions:
          locations
            ?.filter((_, idx) => idx > 0)
            .map((location, idx) => {
              return {
                value: location.id,
                label: location.name,
              }
            }) ?? [],
        companyFirebaseIdOptions: allLocations.slice(1).map(location => ({
          value: location.id,
          label: location.name,
        })),
        refresh: () => {
          setRefire(prevVal => prevVal + 1)
        },
        userCanSeeAllLocations,
        getHierarchySelection: () => {
          const params: HierarchySelection = {
            locationId: undefined,
            locationFirebaseId: undefined,
            regionId: undefined,
            companyFirebaseId: undefined,
            buildingId: undefined,
            companyId: undefined,
          }

          if (locations && selectedBuildingIndex > 0) {
            params.buildingId = locations[selectedLocationIndex].buildings[selectedBuildingIndex].id
          } else if (locations && selectedLocationIndex > 0) {
            params.locationFirebaseId = locations[selectedLocationIndex].id
            params.locationId = locations[selectedLocationIndex].locationPgId
          } else if (selectedRegionIndex > 0) {
            params.regionId = regions[selectedRegionIndex].id
          } else {
            params.companyId = allLocations?.[1]?.companyPgId
            params.companyFirebaseId = session.user?.companyId
          }

          return params
        },
        getSelectedMainDevicesIds: (locationIndex = selectedLocationIndex, buildingIndex = selectedBuildingIndex) => {
          if (!locations) return []
          if (!locations[locationIndex]) return []

          const buildings = locations[locationIndex].buildings
          const building = buildings[buildingIndex]

          const deviceIds =
            buildingIndex === 0
              ? (buildings
                  .filter(isBuilding)
                  .map(building => building.deviceId)
                  .filter(Boolean) as string[])
              : building && isBuilding(building) && building.deviceId
                ? building.deviceIds
                : []

          return deviceIds ?? []
        },
        /**
         * Get all devices from selected region/location/building. If no location index is passed, will return all devices from all locations
         */
        getDevices: (regionIndex = 0, locationIndex = 0, buildingIndex = 0) => {
          // Ensure that the region and location indices are valid
          if (!regions?.length) return []
          if (regionIndex >= regions.length) {
            console.debug(`[LocationsContext - getDevices] Invalid region index (${regionIndex}), index outside array bounds`)
            return []
          }

          const locationsOfSelectedRegion = regions[regionIndex]?.locations
          if (!locationsOfSelectedRegion?.length) return []
          if (locationIndex >= locationsOfSelectedRegion.length) {
            console.debug(`[LocationsContext - getDevices] Invalid location index (${locationIndex}), index outside array bounds`)
            return []
          }
          ///=====================>

          // Case 1: Any region, all locations selected
          if (locationIndex === 0) {
            const locationsWithoutAllLocation = locationsOfSelectedRegion.filter(isLocation)
            return locationsWithoutAllLocation.flatMap(location => location.devices ?? [])
          }

          const selectedLocation = locationsOfSelectedRegion[locationIndex]
          if (!isLocation(selectedLocation)) return [] // At this point the selected location should be a specific location since locationIndex will be > 0, its just a type guard so ts understands it.
          // Case 2: Any region, Specific location  & all buildings selected
          if (buildingIndex === 0) return selectedLocation.devices ?? []

          // Case 3: Any region), specific location & specific building selected
          if (!selectedLocation.buildings[buildingIndex]) return []
          const buildingId = selectedLocation.buildings[buildingIndex].id
          return selectedLocation.devices.filter(device => device.buildingId === buildingId)
        },
      }}
    >
      {children}
    </LocationsContext.Provider>
  )
}

const getEnrichedLocationsInfo = (locationsInfo: LocationsInfoResponse, companyName): EnrichedLocationsInfo => {
  const enrichDevices = (location: LocationsInfoResponse['locations'][0] | LocationsInfoResponse['locations'][1]) => {
    const enrichedDevices: EnrichedDevice[] = location.devices.map(device => {
      const buildingName = location.buildings.find(building => isBuilding(building) && building.id === device.buildingId)?.name || ''
      return {
        ...device,
        buildingName: buildingName,
        locationId: location.id,
        locationPgId: location.locationPgId,
        locationName: location.name,
        companyId: location.id,
      }
    })
    return enrichedDevices
  }

  const enrichLocationsOfRegion = (region: LocationsInfoResponse['regions'][0]) => {
    const enrichedLocationsOfRegion: EnrichedLocations = [{ ...region.locations[0] }]

    for (let i = 1; i < region.locations.length; i++) {
      const location = region.locations[i] as LocationsInfoResponse['locations'][1] // casting since ts does not tell that we are in the index 1 of the array already
      const enrichedDevices: EnrichedDevice[] = enrichDevices(location)
      const enrichedLocation: EnrichedLocation = { ...location, devices: enrichedDevices }
      enrichedLocationsOfRegion.push(enrichedLocation)
    }
    return enrichedLocationsOfRegion
  }

  // Enrich devices from locations
  const AllLocationsLocation = { ...locationsInfo.locations[0] }
  const enrichedLocations: EnrichedLocations = [AllLocationsLocation]
  for (let i = 1; i < locationsInfo.locations.length; i++) {
    const location = locationsInfo.locations[i] as LocationsInfoResponse['locations'][1]
    const enrichedDevices: EnrichedDevice[] = enrichDevices(location)
    enrichedLocations.push({ ...location, devices: enrichedDevices })
  }

  // Enrich devices from regions
  const enrichedRegions: EnrichedRegions | [] = [
    {
      name: companyName || t('common.group'),
      id: 'allGroups',
      locations: enrichedLocations,
    },
  ]

  if (locationsInfo.regions.length) {
    for (let i = 0; i < locationsInfo.regions.length; i++) {
      const enrichedLocationsOfRegion = enrichLocationsOfRegion(locationsInfo.regions[i])
      enrichedRegions.push({ ...locationsInfo.regions[i], locations: enrichedLocationsOfRegion })
    }
  }

  return { ...locationsInfo, locations: enrichedLocations, regions: enrichedRegions }
}
